/**
 * `neuro:assistant:ask` — how a page hands the assistant a question.
 *
 * A page dispatches `new CustomEvent('neuro:assistant:ask', { detail: { prompt } })`
 * on window (Web Search's "Ask Neuro about this page" does). The listener
 * marks the event `detail.handled = true` — dispatch is synchronous — so the
 * page can tell an older host that doesn't listen, and passes the trimmed,
 * capped prompt on. AssistantOverlayHost opens the panel and sends it.
 */

export const ASSISTANT_ASK_EVENT = 'neuro:assistant:ask';
export const ASSISTANT_ASK_MAX_CHARS = 2000;

export interface AssistantAskDetail {
  prompt?: unknown;
  /** Who asked (e.g. 'searxng-search'); informational. */
  source?: unknown;
  /** Set by the listener when it takes the question. */
  handled?: boolean;
}

/** The prompt to send for an ask, or null when it has none. */
export function askPrompt(detail: AssistantAskDetail | null | undefined): string | null {
  const prompt = typeof detail?.prompt === 'string' ? detail.prompt.trim() : '';
  return prompt ? prompt.slice(0, ASSISTANT_ASK_MAX_CHARS) : null;
}

/** Call `onAsk` for every ask with a prompt; returns the unsubscribe. */
export function listenForAssistantAsks(onAsk: (prompt: string) => void, target: Window = window): () => void {
  function handle(event: Event) {
    const detail = (event as CustomEvent<AssistantAskDetail>).detail;
    const prompt = askPrompt(detail);
    if (!prompt) return;
    if (detail && typeof detail === 'object') detail.handled = true;
    onAsk(prompt);
  }
  target.addEventListener(ASSISTANT_ASK_EVENT, handle);
  return () => target.removeEventListener(ASSISTANT_ASK_EVENT, handle);
}
